import { useMutation, useQueryCache } from "react-query";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { putNotifySubscriptionPreferences } from "../../../queries/notify";
import { buildNotifyContactsQueryKey } from "./useInfiniteNotifyContacts";
import { buildNotifyInfiniteSubscriberContactMethodsQueryKey } from "./useInfiniteNotifySubscriberContactMethods";
import { buildNotifySubscriberContactMethodQueryKey } from "./useNotifySubscriberContactMethod";
import { buildNotifySubscriberContactMethodsQueryKey } from "./useNotifySubscriberContactMethodsForLoggedInUser";

interface IUseNotifySubscriptionPreferenceUpdate {
  onSuccess?: (
    res: {
      subscriberContactMethodId: number;
      subscriptionGroupId: number;
      subscriptionGroup: { name: string };
      type: string;
    }[],
  ) => void;
  onError?: (error: unknown) => void;
}

export function useNotifySubscriptionPreferencesUpdate({
  onSuccess,
  onError,
}: IUseNotifySubscriptionPreferenceUpdate = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();

  const [editSubscriptionPreferencesMutation, { isLoading: isEditSubscriptionPreferencesLoading }] = useMutation(
    putNotifySubscriptionPreferences(networkHeaders),
    {
      onSuccess: async (data) => {
        await queryCache.invalidateQueries(buildNotifyInfiniteSubscriberContactMethodsQueryKey());
        await queryCache.invalidateQueries(buildNotifyContactsQueryKey());
        await queryCache.invalidateQueries(buildNotifySubscriberContactMethodsQueryKey());
        await Promise.all(
          data.map(({ subscriberContactMethodId }) =>
            queryCache.invalidateQueries(buildNotifySubscriberContactMethodQueryKey({ id: subscriberContactMethodId })),
          ),
        );
        if (onSuccess) {
          onSuccess(data);
        }
      },
      onError: onError,
    },
  );

  const editSubscriptionPreferences = async ({
    subscriberContactMethodIds,
    subscriptionGroupIds,
    type,
    overwriteExistingPreference = false,
    isAddingToGroup,
  }: {
    subscriberContactMethodIds: number[];
    subscriptionGroupIds: number[];
    type: "Subscribe" | "Unsubscribe";
    overwriteExistingPreference?: boolean;
    isAddingToGroup?: boolean;
  }) => {
    return await editSubscriptionPreferencesMutation({
      subscriberContactMethodIds,
      subscriptionGroupIds,
      type,
      overwriteExistingPreference,
      isAddingToGroup,
    });
  };

  return { editSubscriptionPreferences, isEditSubscriptionPreferencesLoading };
}
